import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { TeamsService } from '../../services/teams.service';
import * as TeamsActions from './teams.actions';
import { TeamsPagination } from '../../models/team.model'; // Corrected import for TeamsPagination
import { TeamMemberService } from '@/services/team-membership.service';

@Injectable()
export class TeamsEffects {
  private actions$ = inject(Actions);
  private teamsService = inject(TeamsService);
  private store = inject(Store);
  private teamMemberService = inject(TeamMemberService);

  loadTeams$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.loadTeams),
      switchMap(({ page, limit, name }) => {
        console.log(`TeamsEffects: Fetching teams. Page: ${page}, Limit: ${limit}, Name: ${name || 'none'}`);
        return this.teamsService.getTeams(page, limit, name).pipe(
          tap((paginationResp) => console.log('TeamsEffects: Received pagination from service:', paginationResp)),
          map((paginationResp: TeamsPagination) =>
            TeamsActions.loadTeamsSuccess({
              pagination: paginationResp,
              totalItems: paginationResp.total, // Pass total from response
            })
          ),
          catchError((error) => {
            console.error('TeamsEffects: Error loading teams:', error);
            return of(TeamsActions.loadTeamsFailure({ error }));
          })
        );
      })
    )
  );

  loadTeamById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.loadTeamById),
      mergeMap((action) =>
        this.teamsService.getTeamById(action.id).pipe(
          map((team) => TeamsActions.loadTeamByIdSuccess({ team })),
          catchError((error) => of(TeamsActions.loadTeamByIdFailure({ error })))
        )
      )
    )
  );

  createTeam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.createTeam),
      tap((action) => console.log('Create team effect triggered with:', action)),
      switchMap(({ team, teamTypeId, parentId }) =>
        this.teamsService.createTeam(team, teamTypeId, parentId).pipe(
          tap((response) => console.debug('Create team response:', response)),
          map((createdTeam) => TeamsActions.createTeamSuccess({ team: createdTeam })),
          catchError((error) => {
            console.error('Error creating team:', error);
            return of(TeamsActions.createTeamFailure({ error }));
          })
        )
      )
    )
  );

  updateTeam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.updateTeam),
      switchMap(({ teamId, updates }) =>
        this.teamsService.updateTeam(teamId, updates).pipe(
          map((updatedTeam) => TeamsActions.updateTeamSuccess({ team: updatedTeam })),
          catchError((error) => {
            console.error('Error updating team:', error);
            return of(TeamsActions.updateTeamFailure({ error }));
          })
        )
      )
    )
  );

  updateTeamFinanceSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.updateTeamFinanceSettings),
      switchMap(({ teamId, settings }) =>
        this.teamsService.updateTeamFinanceSettings(teamId, settings).pipe(
          map((team) => TeamsActions.updateTeamFinanceSettingsSuccess({ team })),
          catchError((error) => {
            console.error('Error updating team finance settings:', error);
            return of(TeamsActions.updateTeamFinanceSettingsFailure({ error }));
          })
        )
      )
    )
  );

  updateTeamReviewerSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.updateTeamReviewerSettings),
      switchMap(({ teamId, settings }) =>
        this.teamsService.updateTeamReviewerSettings(teamId, settings).pipe(
          map((team) => TeamsActions.updateTeamReviewerSettingsSuccess({ team })),
          catchError((error) => {
            console.error('Error updating team reviewer settings:', error);
            return of(TeamsActions.updateTeamReviewerSettingsFailure({ error }));
          })
        )
      )
    )
  );

  // Add Team Member
  addTeamMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.addTeamMember),
      switchMap(({ teamId, userId, roles }) =>
        this.teamMemberService.addMember(teamId, userId, roles).pipe(
          map((member) =>
            TeamsActions.addTeamMemberSuccess({
              teamId,
              member,
            })
          ),
          catchError((error) => of(TeamsActions.addTeamMemberFailure({ error })))
        )
      )
    )
  );

  // Team Member Success/Failure effects for notifications
  addTeamMemberSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TeamsActions.addTeamMemberSuccess),
        map(() => {
          // Here you could dispatch actions for notifications
          // Or use a notification service directly
          console.log('Team member added successfully');
        })
      ),
    { dispatch: false }
  );

  addTeamMemberFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TeamsActions.addTeamMemberFailure),
        map(({ error }) => {
          console.error('Failed to add team member', error);
        })
      ),
    { dispatch: false }
  );

  // Add the missing loadTeamMembers effect
  loadTeamMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.loadTeamMembers),
      mergeMap(({ teamId }) =>
        this.teamMemberService.getTeamMembers(teamId).pipe(
          map((members) => TeamsActions.loadTeamMembersSuccess({ teamId, members })),
          catchError((error) => of(TeamsActions.loadTeamMembersFailure({ error })))
        )
      )
    )
  );

  // Remove Team Member effect
  removeTeamMember$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TeamsActions.removeTeamMember),
      switchMap(({ teamId, userId, membershipId }) =>
        this.teamMemberService.removeMember(teamId, userId, membershipId).pipe(
          map(() =>
            TeamsActions.removeTeamMemberSuccess({
              teamId,
              userId,
              membershipId,
            })
          ),
          catchError((error) => of(TeamsActions.removeTeamMemberFailure({ error })))
        )
      )
    );
  });

  // Final Reviewer effects
  loadFinalReviewers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.loadFinalReviewers),
      mergeMap(({ teamId }) =>
        this.teamMemberService.getFinalReviewers(teamId).pipe(
          map((finalReviewers) => TeamsActions.loadFinalReviewersSuccess({ teamId, finalReviewers })),
          catchError((error) => of(TeamsActions.loadFinalReviewersFailure({ error })))
        )
      )
    )
  );

  addFinalReviewer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.addFinalReviewer),
      switchMap(({ teamId, userId }) =>
        this.teamMemberService.addFinalReviewer(teamId, userId).pipe(
          map((finalReviewer) =>
            TeamsActions.addFinalReviewerSuccess({
              teamId,
              finalReviewer,
            })
          ),
          catchError((error) => of(TeamsActions.addFinalReviewerFailure({ error })))
        )
      )
    )
  );

  removeFinalReviewer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.removeFinalReviewer),
      switchMap(({ teamId, userId }) =>
        this.teamMemberService.removeFinalReviewer(teamId, userId).pipe(
          map(() =>
            TeamsActions.removeFinalReviewerSuccess({
              teamId,
              userId,
            })
          ),
          catchError((error) => of(TeamsActions.removeFinalReviewerFailure({ error })))
        )
      )
    )
  );

  // Reload final reviewers after successful add or remove operations
  reloadFinalReviewersAfterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.addFinalReviewerSuccess, TeamsActions.removeFinalReviewerSuccess),
      map(({ teamId }) => TeamsActions.loadFinalReviewers({ teamId }))
    )
  );

  // Finance Reviewer effects
  loadFinanceReviewers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.loadFinanceReviewers),
      mergeMap(({ teamId }) =>
        this.teamMemberService.getFinanceReviewers(teamId).pipe(
          map((financeReviewers) => TeamsActions.loadFinanceReviewersSuccess({ teamId, financeReviewers })),
          catchError((error) => of(TeamsActions.loadFinanceReviewersFailure({ error })))
        )
      )
    )
  );

  addFinanceReviewer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.addFinanceReviewer),
      switchMap(({ teamId, userId }) =>
        this.teamMemberService.addFinanceReviewer(teamId, userId).pipe(
          map((financeReviewer) =>
            TeamsActions.addFinanceReviewerSuccess({
              teamId,
              financeReviewer,
            })
          ),
          catchError((error) => of(TeamsActions.addFinanceReviewerFailure({ error })))
        )
      )
    )
  );

  removeFinanceReviewer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.removeFinanceReviewer),
      switchMap(({ teamId, userId }) =>
        this.teamMemberService.removeFinanceReviewer(teamId, userId).pipe(
          map(() =>
            TeamsActions.removeFinanceReviewerSuccess({
              teamId,
              userId,
            })
          ),
          catchError((error) => of(TeamsActions.removeFinanceReviewerFailure({ error })))
        )
      )
    )
  );

  // Reload finance reviewers after successful add or remove operations
  reloadFinanceReviewersAfterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.addFinanceReviewerSuccess, TeamsActions.removeFinanceReviewerSuccess),
      map(({ teamId }) => TeamsActions.loadFinanceReviewers({ teamId }))
    )
  );

  // Gemba Lead effects
  loadGembaLeads$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.loadGembaLeads),
      mergeMap(({ teamId }) =>
        this.teamMemberService.getGembaLeads(teamId).pipe(
          map((gembaLeads) => TeamsActions.loadGembaLeadsSuccess({ teamId, gembaLeads })),
          catchError((error) => of(TeamsActions.loadGembaLeadsFailure({ error })))
        )
      )
    )
  );

  addGembaLead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.addGembaLead),
      switchMap(({ teamId, userId }) =>
        this.teamMemberService.addGembaLead(teamId, userId).pipe(
          map((gembaLead) =>
            TeamsActions.addGembaLeadSuccess({
              teamId,
              gembaLead,
            })
          ),
          catchError((error) => of(TeamsActions.addGembaLeadFailure({ error })))
        )
      )
    )
  );

  removeGembaLead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.removeGembaLead),
      switchMap(({ teamId, userId }) =>
        this.teamMemberService.removeGembaLead(teamId, userId).pipe(
          map(() =>
            TeamsActions.removeGembaLeadSuccess({
              teamId,
              userId,
            })
          ),
          catchError((error) => of(TeamsActions.removeGembaLeadFailure({ error })))
        )
      )
    )
  );

  reloadGembaLeadsAfterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.addGembaLeadSuccess, TeamsActions.removeGembaLeadSuccess),
      map(({ teamId }) => TeamsActions.loadGembaLeads({ teamId }))
    )
  );

  // Workspace Lead effects
  loadWorkspaceLeads$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.loadWorkspaceLeads),
      mergeMap(({ teamId }) =>
        this.teamMemberService.getWorkspaceLeads(teamId).pipe(
          map((workspaceLeads) => TeamsActions.loadWorkspaceLeadsSuccess({ teamId, workspaceLeads })),
          catchError((error) => of(TeamsActions.loadWorkspaceLeadsFailure({ error })))
        )
      )
    )
  );

  updateTeamWorkspaceManagementSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.updateTeamWorkspaceManagementSettings),
      switchMap(({ teamId, settings }) =>
        this.teamsService.updateTeamWorkspaceManagementSettings(teamId, settings).pipe(
          map((team) => TeamsActions.updateTeamWorkspaceManagementSettingsSuccess({ team })),
          catchError((error) => {
            console.error('Error updating team workspace management settings:', error);
            return of(TeamsActions.updateTeamWorkspaceManagementSettingsFailure({ error }));
          })
        )
      )
    )
  );

  // Refresh the staged leads snapshot after a combined settings save lands.
  reloadWorkspaceLeadsAfterSettingsUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamsActions.updateTeamWorkspaceManagementSettingsSuccess),
      map(({ team }) => TeamsActions.loadWorkspaceLeads({ teamId: team.id }))
    )
  );

  // ... other team member effects
}
